import * as React from 'react';
import { GenericChartComponent, getMouseCanvas } from '@react-financial-charts/core';
import type { MoreProps } from '@/types/commontypes';
import { calcRatioBoxWidth } from './TPSLRatioBox';
import type { OrderEntryBox } from './OrderEntryBox';
import type { TPAndSLBox } from './TPAndSLBox';
import { BOX_HEIGHT } from '@/utils/canvasUtils';

export interface EdgeCircleAndVerticalLineProps {
  /** entry 서클 y값 (진입가) */
  readonly entryY: number;
  /** TP 서클 y값. undefined면 서클 미표시 */
  readonly tpY: number | undefined;
  /** SL 서클 y값. undefined면 서클 미표시 */
  readonly slY: number | undefined;
  /** TP가 고정됐는지 (수직선 표시 여부 결정) */
  readonly tpIsFixed: boolean;
  /** SL이 고정됐는지 (수직선 표시 여부 결정) */
  readonly slIsFixed: boolean;
  /** 호버 상태 fallback (mouseXY 없을 때 사용) */
  readonly showCircle: boolean;
  /** 서클/수직선 색상 */
  readonly stroke: string;
  readonly strokeWidth: number;
  readonly fill: string;
  /** entry 박스 왼쪽 끝 → 차트 우측 거리 */
  readonly entryLeftToYAxisDistance: number;
  /** ratio 박스 오른쪽 끝 → 차트 우측 거리 */
  readonly ratioRightToYAxisDistance: number;
  /** 헷지 모드 여부 */
  readonly isHedge: boolean | undefined;
  /** 개별 ratio 박스 숨김 여부 (헷지 공통 박스 사용 시) */
  readonly hideRatioBox: boolean | undefined;
  /** Entry 박스 ref — hitTest용 */
  readonly orderEntryBoxRef: React.RefObject<OrderEntryBox | null>;
  /** TP 박스 ref — hitTest용 */
  readonly tpBoxRef: React.RefObject<TPAndSLBox | null>;
  /** SL 박스 ref — hitTest용 */
  readonly slBoxRef: React.RefObject<TPAndSLBox | null>;
}

/**
 * Entry·TP·SL 서클 3개와 이를 잇는 수직 보조선을 그린다.
 *
 * canvasDraw 안에서 직접 hitTest를 수행해 amIOnTop 경쟁을 우회한다.
 * (react-financial-charts의 onHover → setState → showCircle 체인에 의존하지 않음)
 */
export class EdgeCircleAndVerticalLine extends React.Component<EdgeCircleAndVerticalLineProps> {
  render() {
    const {
      entryY,
      tpY,
      slY,
      tpIsFixed,
      slIsFixed,
      showCircle,
      stroke,
      strokeWidth,
      fill,
      entryLeftToYAxisDistance,
      ratioRightToYAxisDistance,
      isHedge,
      hideRatioBox,
      orderEntryBoxRef,
      tpBoxRef,
      slBoxRef,
    } = this.props;

    const hideIndividualRatioBox = isHedge && hideRatioBox;
    const ratioBoxWidth = hideIndividualRatioBox ? 0 : calcRatioBoxWidth();

    /** 박스 좌표와 마우스 위치를 비교해 호버 여부를 판정한다. */
    const calcIsHovering = (mouseXY: [number, number] | undefined): boolean => {
      if (!mouseXY) return showCircle;
      const [mx, my] = mouseXY;
      const entry = orderEntryBoxRef.current;
      if (
        entry &&
        mx >= entry.boxX &&
        mx <= entry.boxX + entry.totalW &&
        my >= entry.boxY &&
        my <= entry.boxY + BOX_HEIGHT
      )
        return true;
      const tp = tpBoxRef.current;
      if (tp && mx >= tp.boxX && mx <= tp.boxX + tp.totalW && my >= tp.boxY && my <= tp.boxY + BOX_HEIGHT) return true;
      const sl = slBoxRef.current;
      if (sl && mx >= sl.boxX && mx <= sl.boxX + sl.totalW && my >= sl.boxY && my <= sl.boxY + BOX_HEIGHT) return true;
      return showCircle;
    };

    const calcCx = (width: number): number =>
      hideIndividualRatioBox
        ? width - entryLeftToYAxisDistance - 8
        : width - ratioRightToYAxisDistance - ratioBoxWidth - 8;

    const drawCircle = (ctx: CanvasRenderingContext2D, moreProps: MoreProps, y: number) => {
      const {
        chartConfig: { width, yScale, height },
      } = moreProps;
      const cy = Math.round(yScale(y));
      if (cy < 0 || cy > height) return;
      const cx = calcCx(width);
      ctx.beginPath();
      ctx.arc(cx, cy, 2, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.lineWidth = strokeWidth;
      ctx.strokeStyle = stroke;
      ctx.stroke();
    };

    return (
      <>
        {/* entry 서클 */}
        <GenericChartComponent
          key="entry-circle"
          clip={false}
          canvasToDraw={getMouseCanvas}
          drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
          canvasDraw={(ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
            if (!calcIsHovering(moreProps.mouseXY)) return;
            drawCircle(ctx, moreProps, entryY);
          }}
        />

        {/* TP/SL 서클: 항상 mount — unmount 시 RFC 구독 순서가 밀려 z-order 깨짐 방지. 표시 조건은 canvasDraw 안에서 처리 */}
        <GenericChartComponent
          key="tp-circle"
          clip={false}
          canvasToDraw={getMouseCanvas}
          drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
          canvasDraw={(ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
            if (!tpIsFixed || tpY === undefined) return;
            if (!calcIsHovering(moreProps.mouseXY)) return;
            drawCircle(ctx, moreProps, tpY);
          }}
        />

        {/* SL 서클 (위 주석 참조) */}
        <GenericChartComponent
          key="sl-circle"
          clip={false}
          canvasToDraw={getMouseCanvas}
          drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
          canvasDraw={(ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
            if (!slIsFixed || slY === undefined) return;
            if (!calcIsHovering(moreProps.mouseXY)) return;
            drawCircle(ctx, moreProps, slY);
          }}
        />

        {/* 수직 보조선: TP·SL 모두 고정됐을 때만 표시 */}
        {slIsFixed && tpIsFixed && (
          <GenericChartComponent
            key="vertical-line"
            clip={true}
            canvasToDraw={getMouseCanvas}
            drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
            canvasDraw={(ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
              if (!calcIsHovering(moreProps.mouseXY)) return;
              const {
                chartConfig: { width, yScale },
              } = moreProps;
              const cx = calcCx(width);
              const y1 = Math.round(yScale(slY ?? entryY));
              const y2 = Math.round(yScale(tpY ?? entryY));
              ctx.save();
              ctx.beginPath();
              ctx.strokeStyle = stroke;
              ctx.lineWidth = strokeWidth;
              ctx.setLineDash([]);
              ctx.moveTo(cx, y1);
              ctx.lineTo(cx, y2);
              ctx.stroke();
              ctx.restore();
            }}
          />
        )}
      </>
    );
  }
}
